// Modernized SearchModal.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faFileUpload,
  faClipboardList,
  faPlus,
  faHospital,
  faHospitalUser,
  faUserMd,
  faUserPlus,
  faCalendarCheck,
  faFileMedical,
  faNotesMedical,
  faMoneyCheckAlt,
  faBuilding,
  faTasks,
  faTools,
  faTable,
  faSearch,
  faTimes,
  faUsers,
  faInbox,
  faSyncAlt,
  faStethoscope,
  faClinicMedical,
  faUserDoctor,
  faFilePrescription,
  faBookMedical,
  faCoins,
  faSlidersH,
  faFileContract,
  faFileInvoice,
  faCloudUploadAlt,
  faFolderPlus,
  faFileInvoiceDollar,
  faClipboardCheck,
  faFileAlt,
  faListAlt,
  faWrench,
  faBell,
  faBan,
  faArrowRight,
  faKeyboard,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useSearch } from "../contexts/SearchContext";

const SearchModal = () => {
  const navigate = useNavigate();
  const { isSearchOpen: isOpen, setIsSearchOpen: setIsOpen } = useSearch();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef();
  const inputRef = useRef();
  const resultsRef = useRef();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "g") {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
        setSelectedIndex(-1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchTerm]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current) {
      const items = resultsRef.current.querySelectorAll("[data-search-item]");
      items[selectedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const handleClickOutside = (event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm("");
      setSelectedIndex(-1);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, setIsOpen]);

  const categoryColors = {
    "General": { bg: "bg-slate-100", text: "text-slate-600", accent: "bg-slate-500" },
    "Medical": { bg: "bg-teal-50", text: "text-teal-700", accent: "bg-teal-500" },
    "Corporate": { bg: "bg-purple-50", text: "text-purple-700", accent: "bg-purple-500" },
    "Plan & Coverage": { bg: "bg-amber-50", text: "text-amber-700", accent: "bg-amber-500" },
    "Members": { bg: "bg-blue-50", text: "text-blue-700", accent: "bg-blue-500" },
    "Transactions": { bg: "bg-orange-50", text: "text-orange-700", accent: "bg-orange-500" },
    "Provider Payment": { bg: "bg-sky-50", text: "text-sky-700", accent: "bg-sky-500" },
    "Claims": { bg: "bg-emerald-50", text: "text-emerald-700", accent: "bg-emerald-500" },
    "System Admin": { bg: "bg-indigo-50", text: "text-indigo-700", accent: "bg-indigo-500" },
    "Other": { bg: "bg-rose-50", text: "text-rose-700", accent: "bg-rose-500" },
  };

  const allItems = [
    // General
    { title: "Dashboard", path: "/dashboard", icon: faChartLine, description: "Overview of the system", category: "General" },
    { title: "Reports", path: "/reports", icon: faChartLine, description: "Generate operational reports", category: "General" },
    { title: "Messages", path: "/messages", icon: faInbox, description: "SMS queues, approval requests, and customer feedbacks", category: "General" },

    // Medical
    { title: "Providers", path: "/medical/provider", icon: faClinicMedical, description: "Manage medical providers", category: "Medical" },
    { title: "Provider Claims Submission Settings", path: "/medical/provider-claims-submission-settings", icon: faSlidersH, description: "Update provider submission of claims (contract) days", category: "Medical" },
    { title: "Add New Provider", path: "/medical/add-new-provider", icon: faHospitalUser, description: "Enroll a new provider", category: "Medical" },
    { title: "Physician", path: "/physician", icon: faUserDoctor, description: "Manage physicians", category: "Medical" },
    { title: "Add Physician", path: "/physician/add", icon: faUserPlus, description: "Enroll a new physician", category: "Medical" },
    { title: "Physician Batch Upload", path: "/physician-batch-upload", icon: faFileUpload, description: "Upload physician batch CSV", category: "Medical" },
    { title: "Physician Batch Upload History", path: "/history-physician-batch-upload", icon: faListAlt, description: "View physician batch upload history", category: "Medical" },
    { title: "Physician Batch Processed Data", path: "/processed-physician-batch-upload", icon: faClipboardCheck, description: "View processed physician batch rows", category: "Medical" },
    { title: "APE Scheduler Setup", path: "/ape-scheduler-setup", icon: faCalendarCheck, description: "Setup and schedule APE", category: "Medical" },
    { title: "APE Generated PDF History", path: "/ape-pdf-history", icon: faFileAlt, description: "View generated APE PDFs", category: "Medical" },
    { title: "CPT Rates (Procedures)", path: "/cpt", icon: faFilePrescription, description: "Current Procedural Terminology", category: "Medical" },
    { title: "Add New CPT", path: "/cpt/add-new-cpt", icon: faPlus, description: "Add a new CPT code", category: "Medical" },
    { title: "CPT Batch Upload", path: "/cpt-batch-upload", icon: faFileUpload, description: "Upload CPT batch CSV", category: "Medical" },
    { title: "CPT Batch Upload History", path: "/history-cpt-batch-upload", icon: faListAlt, description: "View CPT batch upload history", category: "Medical" },
    { title: "CPT Batch Processed Data", path: "/processed-cpt-batch-upload", icon: faClipboardCheck, description: "View processed CPT batch rows", category: "Medical" },
    { title: "ICD Codes (Diagnosis)", path: "/icd", icon: faBookMedical, description: "International Classification of Diseases", category: "Medical" },
    { title: "Add ICD", path: "/icd/add", icon: faPlus, description: "Add a new ICD code", category: "Medical" },
    { title: "RVU", path: "/rvu", icon: faCoins, description: "Relative Value Units", category: "Medical" },
    { title: "Add RVU", path: "/rvu/add", icon: faPlus, description: "Add a new RVU", category: "Medical" },

    // Corporate
    { title: "Corporate", path: "/corporate", icon: faBuilding, description: "Manage corporates", category: "Corporate" },
    { title: "Add Corporate", path: "/corporate/add", icon: faPlus, description: "Enroll a new corporate", category: "Corporate" },

    // Plan & Coverage
    { title: "Plan & Coverage Setup", path: "/plan-and-coverage-benefits-setup", icon: faSlidersH, description: "Setup plans and coverages", category: "Plan & Coverage" },
    { title: "Plan & Coverage Viewing", path: "/plan-and-coverage-benefits-view", icon: faFileContract, description: "View plans and coverages", category: "Plan & Coverage" },

    // Members
    { title: "Member List", path: "/members", icon: faUsers, description: "View and manage members", category: "Members" },
    { title: "Employee ID Generator", path: "/member-employee-id-generator", icon: faKeyboard, description: "Generate reserved employee IDs", category: "Members" },
    { title: "Enrollment Uploading", path: "/member-batch-enrollment", icon: faFileUpload, description: "Batch member enrollment upload", category: "Members" },
    { title: "Enrollment Upload History", path: "/history-member-batch-enrollment", icon: faListAlt, description: "View batch member enrollment upload history", category: "Members" },
    { title: "Enrollment Processed Data", path: "/processed-member-batch-enrollment", icon: faClipboardCheck, description: "View processed member enrollment records", category: "Members" },
    { title: "Renewal Upload", path: "/member-batch-renewal-upload", icon: faFileUpload, description: "Batch member renewal upload", category: "Members" },
    { title: "Renewal Upload History", path: "/history-member-batch-renewal-upload", icon: faListAlt, description: "View batch renewal upload history", category: "Members" },
    { title: "Renewal Processed Data", path: "/processed-member-batch-renewal-upload", icon: faClipboardCheck, description: "View processed renewal records", category: "Members" },
    { title: "Change Plan Upload", path: "/member-batch-change-plan-upload", icon: faFileUpload, description: "Batch member change plan upload", category: "Members" },
    { title: "Change Plan Upload History", path: "/history-member-batch-change-plan-upload", icon: faListAlt, description: "View batch change plan upload history", category: "Members" },
    { title: "Change Plan Processed Data", path: "/processed-member-batch-change-plan-upload", icon: faClipboardCheck, description: "View processed change plan records", category: "Members" },
    { title: "Text Alert/Mobile Upload", path: "/member-batch-text-alert-mobile-update-upload", icon: faFileUpload, description: "Batch text alert/mobile update upload", category: "Members" },
    { title: "Text Alert/Mobile Upload History", path: "/history-member-batch-text-alert-mobile-update-upload", icon: faListAlt, description: "View batch text alert/mobile update upload history", category: "Members" },
    { title: "Text Alert/Mobile Processed Data", path: "/processed-member-batch-text-alert-mobile-update-upload", icon: faClipboardCheck, description: "View processed text alert/mobile update records", category: "Members" },
    { title: "Activation Upload", path: "/member-batch-activation-upload", icon: faFileUpload, description: "Batch member activation upload", category: "Members" },
    { title: "Activation Upload History", path: "/history-member-batch-activation-upload", icon: faListAlt, description: "View batch member activation upload history", category: "Members" },
    { title: "Activation Processed Data", path: "/processed-member-batch-activation-upload", icon: faClipboardCheck, description: "View processed activation records", category: "Members" },
    { title: "Deactivation Upload", path: "/member-batch-deactivation-upload", icon: faFileUpload, description: "Batch member deactivation upload", category: "Members" },
    { title: "Deactivation Upload History", path: "/history-member-batch-deactivation-upload", icon: faListAlt, description: "View batch member deactivation upload history", category: "Members" },
    { title: "Deactivation Processed Data", path: "/processed-member-batch-deactivation-upload", icon: faClipboardCheck, description: "View processed deactivation records", category: "Members" },

    // Transactions
    { title: "(All TRN) Transaction List", path: "/transactions", icon: faClipboardList, description: "View all transactions", category: "Transactions" },
    { title: "Tracker", path: "/tracker", icon: faSearch, description: "Track transaction status", category: "Transactions" },
    { title: "Generate LOA Transaction", path: "/generate-loa-transaction", icon: faPlus, description: "Create new LOA transaction", category: "Transactions" },
    { title: "Admission Verification", path: "/transaction-verification", icon: faHospital, description: "In-Patient/Maternity Admission Verification", category: "Transactions" },
    { title: "Discharge Processing", path: "/transaction-discharge", icon: faClipboardCheck, description: "In-Patient/Maternity Discharge", category: "Transactions" },
    { title: "Reimbursement List", path: "/transaction-reimbursement-list", icon: faFileInvoiceDollar, description: "View reimbursements", category: "Transactions" },
    { title: "Add New Reimbursement", path: "/transaction-reimbursement-add", icon: faPlus, description: "Create a new reimbursement", category: "Transactions" },

    // Provider Payment
    { title: "Detailed Payment Report", path: "/transaction-reimbursement-dpr", icon: faFileInvoice, description: "View detailed payment reports", category: "Provider Payment" },
    { title: "Detailed Payment Report (ALL)", path: "/transaction-reimbursement-dpr-all", icon: faFileInvoice, description: "View all detailed payment report records", category: "Provider Payment" },
    { title: "Batch Add Holiday List", path: "/batch-add-holiday-list", icon: faListAlt, description: "View list of added holidays", category: "Provider Payment" },
    { title: "Batch Add Holiday Upload", path: "/batch-add-holiday-upload", icon: faFileUpload, description: "Upload batch add holiday CSV", category: "Provider Payment" },
    { title: "Batch Add Holiday History", path: "/history-batch-add-holiday-upload", icon: faListAlt, description: "View batch add holiday upload history", category: "Provider Payment" },
    { title: "Batch Add Holiday Processed Data", path: "/processed-batch-add-holiday-upload", icon: faClipboardCheck, description: "View processed batch add holiday records", category: "Provider Payment" },
    { title: "(DPR) Validation Dashboard", path: "/dpr-validation-dashboard", icon: faChartLine, description: "DPR validation overview", category: "Provider Payment" },
    { title: "(DPR) Upload Validation CSV", path: "/dpr-validation-upload", icon: faFileUpload, description: "Upload DPR validation CSV", category: "Provider Payment" },
    { title: "(DPR) Upload Validation History", path: "/dpr-validation-upload-history", icon: faListAlt, description: "DPR upload history", category: "Provider Payment" },
    { title: "(DPR) Upload Validation Processed", path: "/dpr-validation-upload-processed", icon: faClipboardCheck, description: "DPR processed uploads", category: "Provider Payment" },
    { title: "(DPR) AI Summary History", path: "/dpr-validation-upload-ai-summary-history", icon: faListAlt, description: "DPR AI-generated summary history", category: "Provider Payment" },
    { title: "(DPR) Validation Support", path: "/dpr-validation-support", icon: faWrench, description: "Correct DPR validation support fields", category: "Provider Payment" },
    { title: "Provider / Physician Bank Validation", path: "/provider-bank-validation", icon: faMoneyCheckAlt, description: "Approve or reject staged bank detail updates", category: "Provider Payment" },
    { title: "Provider Delivery Address Label", path: "/provider-delivery-address-label", icon: faFileAlt, description: "Generate and print provider delivery labels", category: "Provider Payment" },
    { title: "Upload Settlement CSV", path: "/settlement-batch-upload", icon: faFileUpload, description: "Upload settlement CSV batch", category: "Provider Payment" },
    { title: "Settlement CSV History", path: "/settlement-batch-upload-history", icon: faListAlt, description: "Settlement CSV upload history", category: "Provider Payment" },
    { title: "Settlement Email Outbox", path: "/settlement-email-outbox", icon: faInbox, description: "Review and resend settlement email notifications", category: "Provider Payment" },
    { title: "Upload Deposit Slip CSV", path: "/deposit-slip-batch-upload", icon: faFileUpload, description: "Upload deposit slip CSV batch", category: "Provider Payment" },
    { title: "Deposit Slip CSV History", path: "/deposit-slip-batch-upload-history", icon: faListAlt, description: "Deposit slip CSV upload history", category: "Provider Payment" },
    { title: "Deposit Slip / OR Batch Upload", path: "/depo-or-batch-upload", icon: faFileUpload, description: "Upload Deposit Slip / OR files in batch", category: "Provider Payment" },
    { title: "Deposit Slip / OR Upload History", path: "/depo-or-batch-upload-history", icon: faListAlt, description: "Deposit Slip / OR batch upload history", category: "Provider Payment" },

    // Claims
    { title: "Received/Scanned Uploading", path: "/claims/receiving", icon: faCloudUploadAlt, description: "Upload dates for claims batches", category: "Claims" },
    { title: "Receiving Excluded Logs", path: "/claims/receiving/excluded-logs", icon: faListAlt, description: "View excluded receiving logs", category: "Claims" },
    { title: "Claims Batch Registration", path: "/claims/batches", icon: faFolderPlus, description: "Register claims batches", category: "Claims" },
    { title: "Add Claims Batch", path: "/claims/batches/add", icon: faPlus, description: "Create a new claims batch", category: "Claims" },
    { title: "Claims Stale Checker", path: "/claims/stale-checker", icon: faSearch, description: "Check if LOA is stale or still valid", category: "Claims" },
    { title: "Claims Processing", path: "/claims/processing", icon: faFileInvoiceDollar, description: "Process claims", category: "Claims" },
    { title: "For RTP Entry Module", path: "/claims/rtp-entry", icon: faClipboardCheck, description: "Return to provider entry", category: "Claims" },
    { title: "For RTP Downloaded Form", path: "/claims/rtp-downloaded-form-history", icon: faFileAlt, description: "RTP downloaded form history", category: "Claims" },
    { title: "For RTP Final Approval", path: "/claims/rtp-final-approvals", icon: faClipboardCheck, description: "RTP final approvals", category: "Claims" },
    { title: "Extract Claims Queue", path: "/claims/extract-queue", icon: faClipboardList, description: "Review held extraction claims, next steps and retry history", category: "Claims" },
    { title: "Upload Queue - LOA", path: "/claims/upload-queue", icon: faSyncAlt, description: "Monitor and retry LOA uploads", category: "Claims" },
    { title: "Upload Queue - SOA", path: "/claims/upload-queue-soa", icon: faSyncAlt, description: "Monitor and retry SOA uploads", category: "Claims" },
    { title: "Already Batch Audit Logs", path: "/claims/already-batch-audit-logs", icon: faClipboardCheck, description: "Batch audit log records", category: "Claims" },

    // System Admin
    { title: "Banner Alert Settings", path: "/banners", icon: faBell, description: "Manage banner alerts", category: "System Admin" },
    { title: "System Activity Logs", path: "/activity-logs", icon: faListAlt, description: "View system activity logs", category: "System Admin" },
    { title: "cURL Request Logs", path: "/system-admin/curl-request-response-logs", icon: faListAlt, description: "Monitor cURL request and response logs", category: "System Admin" },
    { title: "Extract Claims Logs", path: "/extract-claims-logs", icon: faClipboardList, description: "Review Extract Claims run and step logs", category: "System Admin" },
    { title: "System User Management", path: "/system-users", icon: faUsers, description: "Manage system users", category: "System Admin" },
    { title: "System Settings V2", path: "/system-settings-v2", icon: faSlidersH, description: "System configuration", category: "System Admin" },
    { title: "Vector Database", path: "/vector-database", icon: faTable, description: "Vector database implementation", category: "System Admin" },
    { title: "Auxiliary Tools", path: "/auxiliary-tools", icon: faWrench, description: "Additional utilities", category: "System Admin" },
    { title: "Reference Coverage Master", path: "/ref-master-table-list/ref-coverage-master", icon: faTable, description: "Master table for coverage", category: "System Admin" },
    { title: "Reference Benefit Master", path: "/ref-master-table-list/ref-benefit-master", icon: faTable, description: "Master table for benefits", category: "System Admin" },

    // Other
    { title: "Provider Access (Blocking)", path: "/provider-access", icon: faBan, description: "Manage provider access blocking", category: "Other" },
    { title: "System v2.0 Updates", path: "/change-logs", icon: faClipboardList, description: "View changelog and updates", category: "Other" },
  ];

  const filteredItems = useMemo(() => {
    if (!searchTerm) return allItems;
    const term = searchTerm.toLowerCase();
    return allItems.filter(
      (item) =>
        item.title.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Group items by category when not searching
  const groupedItems = useMemo(() => {
    const groups = {};
    filteredItems.forEach((item) => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    return groups;
  }, [filteredItems]);

  // Flat list for keyboard navigation
  const flatFiltered = filteredItems;

  const handleKeyDownInput = (e) => {
    if (flatFiltered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < flatFiltered.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : flatFiltered.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < flatFiltered.length) {
        const item = flatFiltered[selectedIndex];
        navigate(item.path);
        setIsOpen(false);
        setSearchTerm("");
        setSelectedIndex(-1);
      }
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
    setSearchTerm("");
    setSelectedIndex(-1);
  };

  if (!isOpen) return null;

  // Build the flat index for matching selectedIndex to grouped rendering
  let globalIndex = -1;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-start justify-center pt-[8vh] z-50 animate-fadeIn">
      <div
        ref={searchRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden border border-gray-200/50"
      >
        {/* Search Input */}
        <div className="relative px-5 py-4">
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-300 text-base"
            />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search modules, pages, or categories..."
              className="w-full pl-11 pr-24 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 text-gray-800 bg-gray-50/50 text-sm placeholder-gray-400 transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDownInput}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
              <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-gray-400 bg-gray-100 border border-gray-200 rounded font-mono">
                ESC
              </kbd>
              <button
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
                onClick={() => {
                  setIsOpen(false);
                  setSearchTerm("");
                  setSelectedIndex(-1);
                }}
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm" />
              </button>
            </div>
          </div>
        </div>

        {/* Category count or search summary */}
        {searchTerm && (
          <div className="px-5 pb-2">
            <p className="text-[11px] text-gray-400">
              {flatFiltered.length} result{flatFiltered.length !== 1 ? "s" : ""} found
              {searchTerm && <> for "<span className="text-gray-600 font-medium">{searchTerm}</span>"</>}
            </p>
          </div>
        )}

        {/* Results */}
        <div ref={resultsRef} className="max-h-[55vh] overflow-y-auto" style={{ scrollbarWidth: "thin", scrollbarColor: "#e5e7eb transparent" }}>
          {flatFiltered.length > 0 ? (
            Object.entries(groupedItems).map(([category, items]) => (
              <div key={category}>
                {/* Category Header */}
                <div className="sticky top-0 bg-white/95 backdrop-blur-sm px-5 py-1.5 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${categoryColors[category]?.accent || "bg-gray-400"}`} />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      {category}
                    </span>
                    <span className="text-[10px] text-gray-300">({items.length})</span>
                  </div>
                </div>
                {/* Category Items */}
                {items.map((item) => {
                  globalIndex++;
                  const idx = globalIndex;
                  const colors = categoryColors[item.category] || categoryColors["Other"];
                  return (
                    <button
                      key={item.path}
                      data-search-item
                      className={`w-full text-left px-5 py-2.5 flex items-center gap-3 transition-all duration-150 border-b border-gray-50 last:border-b-0 group
                        ${idx === selectedIndex
                          ? "bg-indigo-50 border-l-2 border-l-indigo-500"
                          : "hover:bg-gray-50/80 border-l-2 border-l-transparent"
                        }`}
                      onClick={() => handleNavigate(item.path)}
                    >
                      <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg ${colors.bg} ${colors.text}`}>
                        <FontAwesomeIcon icon={item.icon} className="text-xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium block text-xs text-gray-800 truncate">{item.title}</span>
                        <p className="text-[11px] text-gray-400 truncate mt-0.5">{item.description}</p>
                      </div>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className={`text-[10px] flex-shrink-0 transition-all duration-200 ${
                          idx === selectedIndex ? "text-indigo-400 translate-x-0" : "text-gray-300 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            ))
          ) : searchTerm ? (
            <div className="py-12 text-center flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faInbox} className="text-lg text-gray-300" />
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium">No results found</p>
                <p className="text-xs text-gray-400 mt-0.5">Try a different keyword or category name</p>
              </div>
            </div>
          ) : (
            <div className="py-12 text-center flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faSearch} className="text-lg text-gray-300" />
              </div>
              <p className="text-sm text-gray-400">Start typing to search...</p>
            </div>
          )}
        </div>

        {/* Footer Hints */}
        <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          <div className="flex items-center gap-4 text-[10px] text-gray-400">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[9px] font-mono">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[9px] font-mono">↵</kbd>
              open
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[9px] font-mono">esc</kbd>
              close
            </span>
          </div>
          <span className="text-[10px] text-gray-300">{allItems.length} modules</span>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;



